import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../contexts/WalletContext';
import { useContract } from '../contexts/ContractContext';
import { useNetwork } from '../contexts/NetworkContext';
import { getContractByAddress, getSignerContractByAddress, parseAmount } from '../contracts/contract';
import { trustlineService } from '../services/trustline';
import { hypercoreService } from '../services/hypercore';
import './VaultAdminFeatures.css';

const VaultAdminFeatures: React.FC = () => {
  const { signer, account, provider } = useWallet();
  const { contractAddress } = useContract();
  const { currentNetwork } = useNetwork();

  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Vault state
  const [frozen, setFrozen] = useState(false);
  const [totalAssets, setTotalAssets] = useState('0');
  const [trustlinePolicy, setTrustlinePolicy] = useState({
    trustlineOnDeposit: false,
    trustlineOnMint: false,
    trustlineOnWithdraw: false,
    trustlineOnRedeem: false,
    trustlineOnTransfer: false,
    trustlineOnSetFrozen: false,
    trustlineOnAdminWithdraw: false,
    trustlineOnAdminDeposit: false,
    trustlineOnAdminWithdrawToHyperCore: false,
  });

  // Form states
  const [adminWithdrawAmount, setAdminWithdrawAmount] = useState('');
  const [adminDepositAmount, setAdminDepositAmount] = useState('');
  const [adminWithdrawToHyperCoreAmount, setAdminWithdrawToHyperCoreAmount] = useState('');
  const [adminWithdrawToHyperCoreDestinationDex, setAdminWithdrawToHyperCoreDestinationDex] = useState<'perps' | 'spot'>('perps');
  const [adminDepositFromHyperCoreAmount, setAdminDepositFromHyperCoreAmount] = useState('');
  const [adminDepositFromHyperCoreSourceDex, setAdminDepositFromHyperCoreSourceDex] = useState<'perps' | 'spot'>('perps');

  const [assetDecimals, setAssetDecimals] = useState(18);
  const [assetSymbol, setAssetSymbol] = useState('Token');
  const [userAssetBalance, setUserAssetBalance] = useState('0'); // User's wallet balance of the asset token

  const checkOwnership = useCallback(async () => {
    if (!provider || !contractAddress || !account) {
      setIsOwner(false);
      return;
    }

    try {
      const contract = getContractByAddress(provider, contractAddress);
      // Check if account has DEFAULT_ADMIN_ROLE
      // DEFAULT_ADMIN_ROLE = 0x0000000000000000000000000000000000000000000000000000000000000000
      const DEFAULT_ADMIN_ROLE = '0x0000000000000000000000000000000000000000000000000000000000000000';
      const hasAdminRole = await contract.hasRole(DEFAULT_ADMIN_ROLE, account);
      setIsOwner(hasAdminRole);
    } catch (err) {
      console.error('Failed to check admin role:', err);
      setIsOwner(false);
    }
  }, [provider, contractAddress, account]);

  const loadVaultState = useCallback(async () => {
    if (!provider || !contractAddress || !currentNetwork) return;

    try {
      const contract = getContractByAddress(provider, contractAddress);
      
      // Check if we're on HyperEVM to conditionally load trustlineOnAdminWithdrawToHyperCore
      const isHyperEVM = currentNetwork.chainId === 999 || currentNetwork.chainId === 998;
      
      const basePromises = [
        contract.frozen(),
        contract.totalAssets(),
        contract.trustlineOnDeposit(),
        contract.trustlineOnMint(),
        contract.trustlineOnWithdraw(),
        contract.trustlineOnRedeem(),
        contract.trustlineOnTransfer(),
        contract.trustlineOnSetFrozen(),
        contract.trustlineOnAdminWithdraw(),
        contract.trustlineOnAdminDeposit(),
        contract.asset(),
      ];

      // Only fetch trustlineOnAdminWithdrawToHyperCore on HyperEVM networks
      // Use try-catch to handle cases where the contract doesn't have this function (older deployments)
      let trustlineOnAdminWithdrawToHyperCore = false;
      if (isHyperEVM) {
        try {
          // Check if the function exists in the contract
          if (contract.trustlineOnAdminWithdrawToHyperCore) {
            trustlineOnAdminWithdrawToHyperCore = await contract.trustlineOnAdminWithdrawToHyperCore();
          }
        } catch (err) {
          // Function doesn't exist in this contract version, default to false
          console.warn('trustlineOnAdminWithdrawToHyperCore not available in contract:', err);
          trustlineOnAdminWithdrawToHyperCore = false;
        }
      }

      const results = await Promise.all(basePromises);
      
      const [
        frozenState,
        totalAssetsValue,
        trustlineOnDeposit,
        trustlineOnMint,
        trustlineOnWithdraw,
        trustlineOnRedeem,
        trustlineOnTransfer,
        trustlineOnSetFrozen,
        trustlineOnAdminWithdraw,
        trustlineOnAdminDeposit,
        assetAddress,
      ] = results;

      setFrozen(frozenState);
      setTotalAssets(totalAssetsValue.toString());
      setTrustlinePolicy({
        trustlineOnDeposit,
        trustlineOnMint,
        trustlineOnWithdraw,
        trustlineOnRedeem,
        trustlineOnTransfer,
        trustlineOnSetFrozen,
        trustlineOnAdminWithdraw,
        trustlineOnAdminDeposit,
        trustlineOnAdminWithdrawToHyperCore,
      });

      // Get asset info and user balance
      if (assetAddress && assetAddress !== ethers.ZeroAddress) {
        try {
          const assetContract = new ethers.Contract(
            assetAddress,
            ['function symbol() view returns (string)', 'function decimals() view returns (uint8)', 'function balanceOf(address) view returns (uint256)'],
            provider
          );
          const promises = [
            assetContract.symbol(),
            assetContract.decimals(),
          ];
          
          // Get user balance if account is available
          if (account) {
            promises.push(assetContract.balanceOf(account));
          }
          
          const results = await Promise.all(promises);
          setAssetSymbol(results[0]);
          setAssetDecimals(Number(results[1]));
          
          if (account) {
            setUserAssetBalance(results[2].toString());
          } else {
            setUserAssetBalance('0');
          }
        } catch (err) {
          console.warn('Failed to fetch asset info:', err);
        }
      }
    } catch (err: any) {
      console.error('Failed to load vault state:', err);
    }
  }, [provider, contractAddress, currentNetwork, account]);

  useEffect(() => {
    checkOwnership();
    loadVaultState();
    const interval = setInterval(() => {
      checkOwnership();
      loadVaultState();
    }, 10000);

    return () => clearInterval(interval);
  }, [checkOwnership, loadVaultState]);

  const handleSetFrozen = async (frozenValue: boolean) => {
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Pre-validate with Trustline if enabled
      if (trustlinePolicy.trustlineOnSetFrozen) {
        const isValid = await trustlineService.validateSetFrozen(
          currentNetwork.chainId,
          account,
          contractAddress,
          frozenValue
        );
        if (!isValid) {
          setError(`Failed to ${frozenValue ? 'freeze' : 'unfreeze'} vault: Trustline validation rejected`);
          setLoading(false);
          return;
        }
      }

      const contract = getSignerContractByAddress(signer, contractAddress);
      const tx = await contract.setFrozen(frozenValue);
      await tx.wait();

      setSuccess(`Vault ${frozenValue ? 'frozen' : 'unfrozen'} successfully!`);
      loadVaultState();
    } catch (err: any) {
      setError(`Failed to ${frozenValue ? 'freeze' : 'unfreeze'} vault: ` + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const amount = parseAmount(adminWithdrawAmount, assetDecimals);

      // Use the connected wallet address
      const withdrawTo = account!;

      // Pre-validate with Trustline if enabled
      if (trustlinePolicy.trustlineOnAdminWithdraw) {
        const isValid = await trustlineService.validateAdminWithdraw(
          currentNetwork.chainId,
          account,
          contractAddress,
          withdrawTo,
          amount
        );
        if (!isValid) {
          setError('Admin withdraw failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      const tx = await contract.adminWithdraw(withdrawTo, amount);
      await tx.wait();

      setSuccess('Admin withdraw successful! Vault has been frozen.');
      setAdminWithdrawAmount('');
      loadVaultState();
    } catch (err: any) {
      setError('Admin withdraw failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to perform the actual admin deposit operation
  const performAdminDeposit = async (amount: string, successMessage?: string) => {
    if (!signer || !contractAddress || !currentNetwork || !account) {
      throw new Error('Missing required parameters for admin deposit');
    }

    const contract = getSignerContractByAddress(signer, contractAddress);
    const assetContract = new ethers.Contract(
      await contract.asset(),
      ['function approve(address spender, uint256 amount) returns (bool)', 'function allowance(address owner, address spender) view returns (uint256)'],
      signer
    );

    // Convert amount string to bigint for comparisons
    const amountBigInt = BigInt(amount);

    // Use the connected wallet address
    const depositFrom = account!;

    // Pre-validate with Trustline if enabled
    if (trustlinePolicy.trustlineOnAdminDeposit) {
      const isValid = await trustlineService.validateAdminDeposit(
        currentNetwork.chainId,
        account,
        contractAddress,
        depositFrom,
        amount
      );
      if (!isValid) {
        throw new Error('Admin deposit failed: Trustline validation rejected');
      }
    }

    // Ensure sufficient allowance (similar to user deposit flow)
    let allowance = await assetContract.allowance(depositFrom, contractAddress);
    if (BigInt(allowance.toString()) < amountBigInt) {
      setSuccess('Approving tokens...');
      const approveTx = await assetContract.approve(contractAddress, amount);
      await approveTx.wait();
      
      // Poll for allowance update to ensure it's propagated
      let retries = 5;
      while (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5s
        allowance = await assetContract.allowance(depositFrom, contractAddress);
        if (BigInt(allowance.toString()) >= amountBigInt) {
          break;
        }
        retries--;
      }
      
      if (BigInt(allowance.toString()) < amountBigInt) {
        throw new Error('Approval transaction confirmed but allowance not updated. Please try again.');
      }
    }

    setSuccess('Admin depositing to vault...');
    const tx = await contract.adminDeposit(depositFrom, amount);
    await tx.wait();

    setSuccess(successMessage || 'Admin deposit successful!');
    loadVaultState();
  };

  const handleAdminDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const amount = parseAmount(adminDepositAmount, assetDecimals);
      await performAdminDeposit(amount);
      setAdminDepositAmount('');
    } catch (err: any) {
      setError('Admin deposit failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if amount is zero or invalid
  const isZeroOrInvalid = useCallback((amount: string): boolean => {
    if (!amount || amount.trim() === '') return true;
    try {
      const parsed = BigInt(parseAmount(amount, assetDecimals));
      return parsed === BigInt(0);
    } catch {
      return true; // Invalid input is treated as zero/invalid
    }
  }, [assetDecimals]);

  // Check if admin withdraw to HyperCore amount exceeds vault balance
  const isAdminWithdrawToHyperCoreInsufficient = useCallback((amount: string): boolean => {
    if (!amount || amount === '0' || !totalAssets) return false;
    try {
      const required = BigInt(parseAmount(amount, assetDecimals));
      const available = BigInt(totalAssets);
      return required > available;
    } catch {
      return false;
    }
  }, [totalAssets, assetDecimals]);

  const isAdminWithdrawToHyperCoreInvalid = adminWithdrawToHyperCoreAmount 
    ? (isZeroOrInvalid(adminWithdrawToHyperCoreAmount) || isAdminWithdrawToHyperCoreInsufficient(adminWithdrawToHyperCoreAmount))
    : true;

  // Check if admin withdraw amount exceeds vault balance
  const isAdminWithdrawInsufficient = useCallback((amount: string): boolean => {
    if (!amount || amount === '0' || !totalAssets) return false;
    try {
      const required = BigInt(parseAmount(amount, assetDecimals));
      const available = BigInt(totalAssets);
      return required > available;
    } catch {
      return false;
    }
  }, [totalAssets, assetDecimals]);

  const isAdminWithdrawInvalid = adminWithdrawAmount 
    ? (isZeroOrInvalid(adminWithdrawAmount) || isAdminWithdrawInsufficient(adminWithdrawAmount))
    : true;

  // Check if admin deposit amount exceeds user's wallet balance
  const isAdminDepositInsufficient = useCallback((amount: string): boolean => {
    if (!amount || amount === '0' || !userAssetBalance) return false;
    try {
      const required = BigInt(parseAmount(amount, assetDecimals));
      const available = BigInt(userAssetBalance);
      return required > available;
    } catch {
      return false;
    }
  }, [userAssetBalance, assetDecimals]);

  const isAdminDepositInvalid = adminDepositAmount 
    ? (isZeroOrInvalid(adminDepositAmount) || isAdminDepositInsufficient(adminDepositAmount))
    : true;

  const handleAdminWithdrawToHyperCore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getSignerContractByAddress(signer, contractAddress);
      const amount = parseAmount(adminWithdrawToHyperCoreAmount, assetDecimals);
      
      // Convert destinationDex: 'perps' -> 0, 'spot' -> type(uint32).max (4294967295)
      const destinationDex = adminWithdrawToHyperCoreDestinationDex === 'perps' ? 0 : 4294967295;

      // Pre-validate with Trustline if enabled
      if (trustlinePolicy.trustlineOnAdminWithdrawToHyperCore) {
        const isValid = await trustlineService.validateAdminWithdrawToHyperCore(
          currentNetwork.chainId,
          account,
          contractAddress,
          amount,
          destinationDex
        );
        if (!isValid) {
          setError('Admin withdraw to HyperCore failed: Trustline validation rejected');
          setLoading(false);
          return;
        }
      }

      const tx = await contract.adminWithdrawToHyperCore(amount, destinationDex);
      await tx.wait();

      setSuccess(`Admin withdraw to HyperCore successful! ${adminWithdrawToHyperCoreDestinationDex === 'perps' ? 'Perps' : 'Spot'} DEX. Vault has been frozen.`);
      setAdminWithdrawToHyperCoreAmount('');
      setAdminWithdrawToHyperCoreDestinationDex('perps');
      loadVaultState();
    } catch (err: any) {
      setError('Admin withdraw to HyperCore failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdminDepositFromHyperCore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Withdraw from HyperCore
      // HyperEVM Mainnet = 999, HyperEVM Testnet = 998
      let hyperliquidChain: 'Mainnet' | 'Testnet';
      if (currentNetwork.chainId === 999) {
        hyperliquidChain = 'Mainnet';
      } else if (currentNetwork.chainId === 998) {
        hyperliquidChain = 'Testnet';
      } else {
        throw new Error(`Unsupported chain ID for HyperCore: ${currentNetwork.chainId}. Must be 999 (Mainnet) or 998 (Testnet).`);
      }
      
      const signatureChainId = `0x${currentNetwork.chainId.toString(16)}`;

      // Step 1: Get initial balance and compute required balance BEFORE withdrawal
      const contract = getSignerContractByAddress(signer, contractAddress);
      const assetAddress = await contract.asset();
      const assetContract = new ethers.Contract(
        assetAddress,
        ['function balanceOf(address) view returns (uint256)', 'function approve(address spender, uint256 amount) returns (bool)', 'function allowance(address owner, address spender) view returns (uint256)'],
        signer
      );

      const amount = parseAmount(adminDepositFromHyperCoreAmount, assetDecimals);
      
      // Get initial balance before withdrawal to avoid race condition
      const initialBalance = await assetContract.balanceOf(account);
      const initialBalanceBigInt = BigInt(initialBalance.toString());
      const requiredBalance = initialBalanceBigInt + BigInt(amount);

      // Step 2: Withdraw from HyperCore
      setSuccess('Withdrawing from HyperCore...');
      
      await hypercoreService.withdrawUSDCFromHyperCore({
        amount: adminDepositFromHyperCoreAmount,
        sourceDex: adminDepositFromHyperCoreSourceDex,
        hyperliquidChain: hyperliquidChain,
        signatureChainId: signatureChainId,
        signer: signer,
      });

      setSuccess('HyperCore withdrawal submitted. Waiting for funds to arrive...');

      // Step 3: Poll balance until funds arrive
      
      // Poll for balance update (max 12 attempts, 5 seconds apart = 60 seconds total)
      const maxRetries = 12;
      const pollInterval = 5000; // 2 seconds
      let retries = 0;
      let balance = initialBalance;
      
      while (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        balance = await assetContract.balanceOf(account);
        const currentBalanceBigInt = BigInt(balance.toString());
        
        if (currentBalanceBigInt >= requiredBalance) {
          setSuccess('Funds received! Proceeding with deposit...');
          break;
        }
        
        retries++;
        if (retries % 2 === 0) {
          // Update status every 10 seconds
          setSuccess(`Waiting for funds... (${retries * pollInterval / 1000}s elapsed)`);
        }
      }
      
      // Final balance check
      const finalBalance = await assetContract.balanceOf(account);
      const finalBalanceBigInt = BigInt(finalBalance.toString());
      
      if (finalBalanceBigInt < requiredBalance) {
        const currentBalanceFormatted = ethers.formatUnits(finalBalance, assetDecimals);
        const expectedBalanceFormatted = ethers.formatUnits(requiredBalance, assetDecimals);
        setError(`Insufficient balance after waiting. Expected at least ${expectedBalanceFormatted} USDC, but only ${currentBalanceFormatted} USDC available. Please wait for the HyperCore withdrawal to complete and try again.`);
        setLoading(false);
        return;
      }

      // Step 4: Perform admin deposit using the shared helper
      await performAdminDeposit(
        amount,
        `Deposit from HyperCore successful! Withdrew from ${adminDepositFromHyperCoreSourceDex === 'perps' ? 'Perps' : 'Spot'} DEX and deposited to vault.`
      );
      
      setAdminDepositFromHyperCoreAmount('');
      setAdminDepositFromHyperCoreSourceDex('perps');
    } catch (err: any) {
      setError('Deposit from HyperCore failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetTrustlinePolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signer || !contractAddress || !currentNetwork || !account) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Pre-validate with Trustline (always required for setTrustlinePolicy)
      // Only pass trustlineOnAdminWithdrawToHyperCore on HyperEVM networks
      const isHyperEVM = currentNetwork.chainId === 999 || currentNetwork.chainId === 998;
      const isValid = await trustlineService.validateSetTrustlinePolicy(
        currentNetwork.chainId,
        account,
        contractAddress,
        trustlinePolicy.trustlineOnDeposit,
        trustlinePolicy.trustlineOnMint,
        trustlinePolicy.trustlineOnWithdraw,
        trustlinePolicy.trustlineOnRedeem,
        trustlinePolicy.trustlineOnTransfer,
        trustlinePolicy.trustlineOnSetFrozen,
        trustlinePolicy.trustlineOnAdminWithdraw,
        trustlinePolicy.trustlineOnAdminDeposit,
        isHyperEVM ? trustlinePolicy.trustlineOnAdminWithdrawToHyperCore : undefined
      );
      if (!isValid) {
        setError('Failed to update Trustline policy: Trustline validation rejected');
        setLoading(false);
        return;
      }

      const contract = getSignerContractByAddress(signer, contractAddress);
      
      // Call setTrustlinePolicy with appropriate parameters based on network
      let tx;
      if (isHyperEVM) {
        // On HyperEVM, include the 9th parameter
        tx = await contract.setTrustlinePolicy(
          trustlinePolicy.trustlineOnDeposit,
          trustlinePolicy.trustlineOnMint,
          trustlinePolicy.trustlineOnWithdraw,
          trustlinePolicy.trustlineOnRedeem,
          trustlinePolicy.trustlineOnTransfer,
          trustlinePolicy.trustlineOnSetFrozen,
          trustlinePolicy.trustlineOnAdminWithdraw,
          trustlinePolicy.trustlineOnAdminDeposit,
          trustlinePolicy.trustlineOnAdminWithdrawToHyperCore
        );
      } else {
        // On other networks, use 8 parameters
        tx = await contract.setTrustlinePolicy(
          trustlinePolicy.trustlineOnDeposit,
          trustlinePolicy.trustlineOnMint,
          trustlinePolicy.trustlineOnWithdraw,
          trustlinePolicy.trustlineOnRedeem,
          trustlinePolicy.trustlineOnTransfer,
          trustlinePolicy.trustlineOnSetFrozen,
          trustlinePolicy.trustlineOnAdminWithdraw,
          trustlinePolicy.trustlineOnAdminDeposit
        );
      }
      await tx.wait();

      setSuccess('Trustline policy updated successfully!');
      loadVaultState();
    } catch (err: any) {
      setError('Failed to update Trustline policy: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOwner) {
    return null; // Don't render if not owner
  }

  return (
    <div className="vault-admin-features">
      <div className="admin-header">
        <h3 className="section-header">Manager Controls</h3>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Freeze Control */}
      <div className="admin-card">
        <h4 className="admin-card-title">Vault Freeze Control</h4>
        <div className="freeze-control">
          <div className="freeze-status">
            <span className="freeze-label">Current Status:</span>
            <span className={`freeze-value ${frozen ? 'frozen' : 'active'}`}>
              {frozen ? '❄️ Frozen' : '✅ Active'}
            </span>
          </div>
          <div className="freeze-buttons">
            <button
              className={`freeze-btn ${frozen ? 'active' : ''}`}
              onClick={() => handleSetFrozen(true)}
              disabled={loading || frozen}
            >
              Freeze Vault
            </button>
            <button
              className={`unfreeze-btn ${!frozen ? 'active' : ''}`}
              onClick={() => handleSetFrozen(false)}
              disabled={loading || !frozen}
            >
              Unfreeze Vault
            </button>
          </div>
        </div>
      </div>

      {/* Admin Withdraw to HyperCore - Only visible on HyperEVM networks */}
      {(currentNetwork?.chainId === 999 || currentNetwork?.chainId === 998) && (
        <div className="admin-card">
          <h4 className="admin-card-title">Manager Withdraw to HyperCore</h4>
          <p className="admin-card-description">
            Withdraw assets from the vault to HyperCore. This will automatically freeze the vault.
            Funds will be credited to your address on HyperCore.
          </p>
          <form onSubmit={handleAdminWithdrawToHyperCore} className="admin-form">
            <div className="form-group">
              <label htmlFor="admin-withdraw-hypercore-amount">Amount ({assetSymbol}):</label>
              <input
                id="admin-withdraw-hypercore-amount"
                type="text"
                value={adminWithdrawToHyperCoreAmount}
                onChange={(e) => setAdminWithdrawToHyperCoreAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>Destination DEX:</label>
              <div className="dex-selector">
                <button
                  type="button"
                  className={`dex-option ${adminWithdrawToHyperCoreDestinationDex === 'perps' ? 'active' : ''}`}
                  onClick={() => setAdminWithdrawToHyperCoreDestinationDex('perps')}
                  disabled={loading}
                >
                  Perps DEX
                </button>
                <button
                  type="button"
                  className={`dex-option ${adminWithdrawToHyperCoreDestinationDex === 'spot' ? 'active' : ''}`}
                  onClick={() => setAdminWithdrawToHyperCoreDestinationDex('spot')}
                  disabled={loading}
                >
                  Spot DEX
                </button>
              </div>
            </div>
            <button 
              type="submit" 
              className="submit-btn admin-withdraw-hypercore-btn" 
              disabled={loading || isAdminWithdrawToHyperCoreInvalid}
            >
              {loading 
                ? 'Processing...' 
                : isAdminWithdrawToHyperCoreInvalid && adminWithdrawToHyperCoreAmount
                  ? (isZeroOrInvalid(adminWithdrawToHyperCoreAmount) ? 'Amount must be greater than 0' : 'Insufficient Balance')
                  : 'Withdraw to HyperCore'
              }
            </button>
          </form>
        </div>
      )}

      {/* Admin Deposit from HyperCore - Only visible on HyperEVM networks */}
      {(currentNetwork?.chainId === 999 || currentNetwork?.chainId === 998) && (
        <div className="admin-card">
          <h4 className="admin-card-title">Manager Deposit from HyperCore</h4>
          <p className="admin-card-description">
            Withdraw USDC from your HyperCore balance (spot or perps) and automatically deposit it to the vault.
            This will first withdraw from HyperCore to your wallet, then deposit to the vault.
          </p>
          <form onSubmit={handleAdminDepositFromHyperCore} className="admin-form">
            <div className="form-group">
              <label htmlFor="admin-deposit-hypercore-amount">Amount (USDC):</label>
              <input
                id="admin-deposit-hypercore-amount"
                type="text"
                value={adminDepositFromHyperCoreAmount}
                onChange={(e) => setAdminDepositFromHyperCoreAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>Source DEX:</label>
              <div className="dex-selector">
                <button
                  type="button"
                  className={`dex-option ${adminDepositFromHyperCoreSourceDex === 'perps' ? 'active' : ''}`}
                  onClick={() => setAdminDepositFromHyperCoreSourceDex('perps')}
                  disabled={loading}
                >
                  Perps DEX
                </button>
                <button
                  type="button"
                  className={`dex-option ${adminDepositFromHyperCoreSourceDex === 'spot' ? 'active' : ''}`}
                  onClick={() => setAdminDepositFromHyperCoreSourceDex('spot')}
                  disabled={loading}
                >
                  Spot DEX
                </button>
              </div>
            </div>
            <button 
              type="submit" 
              className="submit-btn admin-deposit-hypercore-btn" 
              disabled={loading || !adminDepositFromHyperCoreAmount || isZeroOrInvalid(adminDepositFromHyperCoreAmount)}
            >
              {loading 
                ? 'Processing...' 
                : isZeroOrInvalid(adminDepositFromHyperCoreAmount) && adminDepositFromHyperCoreAmount
                  ? 'Amount must be greater than 0'
                  : 'Deposit from HyperCore'
              }
            </button>
          </form>
        </div>
      )}

      {/* Admin Withdraw - Hidden on HyperEVM networks */}
      {currentNetwork && currentNetwork.chainId !== 999 && currentNetwork.chainId !== 998 && (
        <div className="admin-card">
          <h4 className="admin-card-title">Manager Withdraw</h4>
          <p className="admin-card-description">
            Withdraw assets from the vault for investment. This will automatically freeze the vault.
          </p>
          <form onSubmit={handleAdminWithdraw} className="admin-form">
            <div className="form-group">
              <label htmlFor="admin-withdraw-to">To Address:</label>
              <input
                id="admin-withdraw-to"
                type="text"
                value={account || ''}
                placeholder="0x..."
                disabled={true}
                readOnly
              />
              <small className="form-hint">Using your connected wallet address</small>
            </div>
            <div className="form-group">
              <label htmlFor="admin-withdraw-amount">Amount ({assetSymbol}):</label>
              <input
                id="admin-withdraw-amount"
                type="text"
                value={adminWithdrawAmount}
                onChange={(e) => setAdminWithdrawAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading}
              />
            </div>
            <button 
              type="submit" 
              className="submit-btn admin-withdraw-btn" 
              disabled={loading || !account || isAdminWithdrawInvalid}
            >
              {loading 
                ? 'Processing...' 
                : isAdminWithdrawInvalid && adminWithdrawAmount
                  ? (isZeroOrInvalid(adminWithdrawAmount) ? 'Amount must be greater than 0' : 'Insufficient Balance')
                  : 'Withdraw Assets'
              }
            </button>
          </form>
        </div>
      )}

      {/* Admin Deposit - Hidden on HyperEVM networks */}
      {currentNetwork && currentNetwork.chainId !== 999 && currentNetwork.chainId !== 998 && (
        <div className="admin-card">
          <h4 className="admin-card-title">Manager Deposit</h4>
          <p className="admin-card-description">
            Deposit assets back into the vault after investment. The source address will be asked to approve this contract first.
          </p>
          <form onSubmit={handleAdminDeposit} className="admin-form">
            <div className="form-group">
              <label htmlFor="admin-deposit-from">From Address:</label>
              <input
                id="admin-deposit-from"
                type="text"
                value={account || ''}
                placeholder="0x..."
                disabled={true}
                readOnly
              />
              <small className="form-hint">Using your connected wallet address</small>
            </div>
            <div className="form-group">
              <label htmlFor="admin-deposit-amount">Amount ({assetSymbol}):</label>
              <input
                id="admin-deposit-amount"
                type="text"
                value={adminDepositAmount}
                onChange={(e) => setAdminDepositAmount(e.target.value)}
                placeholder="0.0"
                disabled={loading}
              />
            </div>
            <button 
              type="submit" 
              className="submit-btn admin-deposit-btn" 
              disabled={loading || !account || isAdminDepositInvalid}
            >
              {loading 
                ? 'Processing...' 
                : isAdminDepositInvalid && adminDepositAmount
                  ? (isZeroOrInvalid(adminDepositAmount) ? 'Amount must be greater than 0' : 'Insufficient Balance')
                  : 'Deposit Assets'
              }
            </button>
          </form>
        </div>
      )}

      {/* Trustline Policy Settings - Temporarily hidden */}
      {false && (
      <div className="admin-card">
        <h4 className="admin-card-title">Trustline Policy Settings</h4>
        <form onSubmit={handleSetTrustlinePolicy} className="policy-form">
          <div className="policy-checkboxes">
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnDeposit}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnDeposit: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Deposit</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnMint}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnMint: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Mint</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnWithdraw}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnWithdraw: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Withdraw</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnRedeem}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnRedeem: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Redeem</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnTransfer}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnTransfer: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Transfer</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnSetFrozen}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnSetFrozen: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Set Frozen</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnAdminWithdraw}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnAdminWithdraw: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Manager Withdraw</span>
            </label>
            <label className="policy-checkbox">
              <input
                type="checkbox"
                checked={trustlinePolicy.trustlineOnAdminDeposit}
                onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnAdminDeposit: e.target.checked })}
                disabled={loading}
              />
              <span>Protect Manager Deposit</span>
            </label>
            {(currentNetwork?.chainId === 999 || currentNetwork?.chainId === 998) && (
              <label className="policy-checkbox">
                <input
                  type="checkbox"
                  checked={trustlinePolicy.trustlineOnAdminWithdrawToHyperCore}
                  onChange={(e) => setTrustlinePolicy({ ...trustlinePolicy, trustlineOnAdminWithdrawToHyperCore: e.target.checked })}
                  disabled={loading}
                />
                <span>Protect Manager Withdraw to HyperCore</span>
              </label>
            )}
          </div>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Updating...' : 'Update Policy'}
          </button>
        </form>
      </div>
      )}
    </div>
  );
};

export default VaultAdminFeatures;

